import { Suspense, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { type IPublicWorkArea } from '@featit/shared/public-read';
import { lazyRoute } from '@/routing/lazyRoute';
import { publicAddressOf } from './publicAddress';
import { usePublicWorkArea } from './usePublicWorkArea';
import { setViewedPublicWorkArea } from './viewedPublicWorkArea';
import { Waiting } from '@/components/Waiting';
import { useT } from '@/i18n/LocaleProvider';

/*
  Its own chunk: every protected screen renders this gate, but only a Public work area's address
  draws the view, so a signed-in launch no longer parses it (about 10 KB of the entry, gate 474a's
  launch-tab-boot budget).
*/
const PublicWorkAreaView = lazyRoute(
  () => import('./PublicWorkAreaView'),
  (module) => module.PublicWorkAreaView,
);

/** The spinner a gate shows while it asks whether an address is public. */
export function PublicLookupPending() {
  const t = useT();
  return (
    <div
      data-route-pending=""
      className="flex justify-center py-16"
      role="status"
      aria-label={t('navigation.opening')}
    >
      <Waiting size="section" silent />
    </div>
  );
}

/**
 * Where a signed-out visitor to `/<namespace>/<slug>` lands before being sent to sign in (F-0338
 * AC-PV21), and where a signed-in person who is not a member of the work area lands before being told
 * it does not exist. One question, asked of the signed-out read surface: is this a Public work area?
 *
 * - yes: the read-only view (`PublicWorkAreaView`) — the list (at the home and at `/-/items`, I-1529) or
 *   an item; at any other route of the work
 *   area (settings, members, a decision …) the same view draws a plain sign-in page, so the address
 *   never shows a list that is not its own (I-1506);
 * - no — and the answer is the same for a private, a missing and a taken-down one (AC-PV6) — the
 *   `fallback`, which is exactly what this address showed before Public existed;
 * - an address that is not a work area's at all (`/acme`, an app route): the `fallback`, asked nothing.
 */
export function PublicWorkAreaGate({
  fallback,
  signedIn,
  signedInAction,
}: {
  fallback: ReactNode;
  signedIn: boolean;
  /** F-0739 (PJ-7): the way in a signed-in non-member reads, for the work area found. */
  signedInAction?: (workArea: IPublicWorkArea) => ReactNode;
}) {
  const location = useLocation();
  const address = publicAddressOf(location.pathname);
  const { lookup } = usePublicWorkArea(address?.reference ?? null);
  // PJ-8: the shell's selector names the Public work area a signed-in non-member reads.
  const viewed = signedIn && address !== null && lookup.kind === 'found' ? lookup.workArea : null;
  useEffect(() => {
    if (!viewed) return;
    setViewedPublicWorkArea({ id: viewed.id, name: viewed.name });
    return () => setViewedPublicWorkArea(null);
  }, [viewed]);

  if (address === null) return <>{fallback}</>;
  if (lookup.kind === 'loading') return <PublicLookupPending />;
  if (lookup.kind === 'found') {
    return (
      <Suspense fallback={<PublicLookupPending />}>
        <PublicWorkAreaView
          workArea={lookup.workArea}
          itemKey={address.itemKey}
          restricted={address.surface === 'private'}
          signedIn={signedIn}
          signedInAction={signedInAction?.(lookup.workArea)}
        />
      </Suspense>
    );
  }
  // Not found, limited or failed: nothing public to show, so what this address always showed.
  return <>{fallback}</>;
}
